import React from "react";

import Iconfont from "@components/iconfont/iconfont";
import MarkdownRenderer from "@components/markdown_renderer";
import NewsCard from "@components/news_card";
import NewsCardCover from "@components/news_card_cover";
import ScrollTopBtn from "@components/scroll_top_btn";

import noDataIcon from "@assets/images/no_data_new_light.svg";

import { formatNewsPublishDate } from "../../shared/news";
import NewsDetailHeader from "../../shared/NewsDetailHeader";
import useNewsDetail from "../../shared/useNewsDetail";
import useNewsRecommend from "../../shared/useNewsRecommend";

import "./NewsDetail.less";

/** 推荐模块标题：相似文章 / 最新内容共用，样式一致 */
function RecommendHead({ title = "相似文章推荐" }) {
  return (
    <div className="news-detail-recommend__head">
      <span className="news-detail-recommend__eyebrow">RELATED</span>
      <h2 className="news-detail-recommend__title">{title}</h2>
    </div>
  );
}

/** 上一篇 / 下一篇卡片：整块可点击；端文章显示为不可点击的灰色按钮 */
function NewsPagerItem({
  article,
  direction = "prev",
  disabled = false,
  placeholder = "",
  onOpenDetail,
}) {
  const isNext = direction === "next";
  const itemTitle = disabled ? placeholder : article?.title || "";
  const summary = disabled ? "" : article?.summary || "";
  const author = disabled ? "" : String(article?.author || "").trim();
  const publishDate = disabled
    ? ""
    : formatNewsPublishDate(article?.tmPublished);
  const label = isNext ? "下一篇" : "上一篇";

  const handleClick = () => {
    if (!article) return;
    onOpenDetail?.(article.id, itemTitle);
  };

  return (
    <button
      type="button"
      className={`news-detail-pager__item${
        isNext ? " news-detail-pager__item--next" : ""
      }${disabled ? " news-detail-pager__item--disabled" : ""}`}
      disabled={disabled}
      onClick={disabled ? undefined : handleClick}
    >
      <span className="news-detail-pager__label">
        {isNext ? null : (
          <Iconfont
            type="icon-a-danjiantouxiangzuoxi"
            className="news-detail-pager__icon"
          />
        )}
        <span>{label}</span>
        {isNext ? (
          <Iconfont
            type="icon-a-danjiantouxiangyouxi"
            className="news-detail-pager__icon"
          />
        ) : null}
      </span>

      <span className="news-detail-pager__body">
        <span className="news-detail-pager__title">{itemTitle}</span>
        <span className="news-detail-pager__summary">{summary}</span>
        <span className="news-detail-pager__meta">
          {author ? (
            <span className="news-detail-pager__author" title={author}>
              {author}
            </span>
          ) : null}
          {author && publishDate ? (
            <span className="news-detail-pager__meta-sep" aria-hidden="true">
              ·
            </span>
          ) : null}
          {publishDate ? (
            <span className="news-detail-pager__date">{publishDate}</span>
          ) : null}
        </span>
      </span>
    </button>
  );
}

/**
 * 资讯详情 / Web + Pad
 *
 * 顶部 nav 下方是面包屑导航：资讯 > 文章标题；第一层点击回到资讯列表。
 * 左栏渲染标题、文章信息和 Markdown 正文，右栏渲染相似文章推荐。
 *
 * @param {string} id 资讯 id
 * @param {string} title 资讯标题，来自列表卡片
 * @param {Function} onBack 返回资讯列表
 * @param {Function} onOpenDetail 点击相似文章 / 上一篇 / 下一篇，进入另一篇详情
 * @param {object} previewArticle 后台预览：管理端详情（可含草稿）
 * @param {boolean} previewLoading 后台预览：管理端详情加载中
 * @param {boolean} previewError 后台预览：管理端详情失败
 * @param {boolean} previewMode 后台预览：所有状态均跳过公开详情和推荐
 */
export default function NewsDetail({
  id,
  title = "",
  onBack,
  onOpenDetail,
  previewArticle = null,
  previewLoading = false,
  previewError = false,
  previewMode = false,
}) {
  const {
    detail: publicDetail,
    loading: publicLoading,
    error: publicError,
  } = useNewsDetail(previewMode ? null : id);
  // 当前后端未提供 /api/admin/news/recommend，预览不请求推荐，也不回退公开接口。
  const { similar, latest, loading: recommendLoading } = useNewsRecommend(
    previewMode ? null : id
  );
  /** 一条有效数据都没有时，最新内容推荐整块（含标题）不渲染 */
  const visibleLatest = latest.filter((item) => item && item.id && item.title);
  const article = previewMode ? previewArticle : publicDetail?.article || null;
  const titleText = String(article?.title || (previewMode ? "" : title) || "").trim();
  const content = String(article?.content || "");
  const loading = previewMode ? previewLoading : publicLoading;
  const error = previewMode ? previewError : publicError;
  const prev = publicDetail?.prev || null;
  const next = publicDetail?.next || null;
  const hasPrev =
    Boolean(publicDetail) && !publicDetail?.prevEnd && Boolean(prev);
  const hasNext =
    Boolean(publicDetail) && !publicDetail?.nextEnd && Boolean(next);
  /** 预览保留上下篇模块；管理端未提供上下篇时，沿用现有禁用态，不请求公开数据补齐。 */
  const showPager = previewMode || Boolean(publicDetail);

  /** 与「立刻获取方案」一致：问卷链接新标签打开，先提交一帧按压态；
   *  后台预览只做视觉还原，点击不打开外链。 */
  const handleSupportOpen = (event) => {
    event.preventDefault();
    if (previewMode) return;
    const target = event.currentTarget;
    target.classList.add("is-pressed");
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        window.open(
          "https://v.wjx.cn/vm/PD6aaEY.aspx#",
          "_blank",
          "noopener,noreferrer"
        );
        setTimeout(() => target.classList.remove("is-pressed"), 200);
      })
    );
  };
  const showRecommendEmpty =
    !recommendLoading && similar.length === 0;
  const detailState = loading
    ? "loading"
    : error
      ? "error"
      : article
        ? "success"
        : "idle";

  return (
    <div
      className="news-page news-detail-page"
      data-news-id={id || ""}
      data-news-state={detailState}
    >
      <nav className="news-detail-breadcrumb" aria-label="面包屑">
        <button
          type="button"
          className="news-detail-breadcrumb__link"
          onClick={onBack}
        >
          资讯
        </button>

        {titleText ? (
          <>
            <Iconfont
              type="icon-a-danjiantouxiangyouxi"
              className="news-detail-breadcrumb__icon"
            />
            <span className="news-detail-breadcrumb__title" title={titleText}>
              {titleText}
            </span>
          </>
        ) : null}
      </nav>

      <div className="news-detail-content">
        <div className="news-detail-main">
          <NewsDetailHeader article={article} previewMode={previewMode} />
          {content.trim() ? (
            <MarkdownRenderer
              markdown={content}
              className="news-detail-markdown"
            />
          ) : loading ? null : (
            <div className="news-detail-main__placeholder">
              {error ? "资讯内容加载失败" : "暂无内容"}
            </div>
          )}
        </div>

        <aside className="news-detail-aside" aria-label="相似文章推荐">
          <section
            className={`news-detail-recommend${
              showRecommendEmpty ? " news-detail-recommend--empty" : ""
            }`}
          >
            <RecommendHead />

            {similar.length > 0 ? (
              <div className="news-detail-recommend__list">
                {similar.map((item) => {
                  const meta = [
                    item.categoryLabel,
                    formatNewsPublishDate(item.tmPublished),
                  ]
                    .filter(Boolean)
                    .join("：");
                  const itemTitle = item.title || "";

                  return (
                    <div
                      key={item.id}
                      className="news-detail-recommend__item"
                      role="button"
                      tabIndex={0}
                      onClick={() => onOpenDetail?.(item.id, itemTitle)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          onOpenDetail?.(item.id, itemTitle);
                        }
                      }}
                    >
                      <div className="news-detail-recommend__cover">
                        <NewsCardCover
                          coverUrl={item.cover || ""}
                          title={itemTitle}
                        />
                      </div>

                      <div className="news-detail-recommend__info">
                        <span
                          className="news-detail-recommend__item-title"
                          title={itemTitle}
                        >
                          {itemTitle}
                        </span>
                        {meta ? (
                          <span className="news-detail-recommend__meta">
                            {meta}
                          </span>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : showRecommendEmpty ? (
              <div className="news-detail-recommend__empty">
                <img
                  className="news-detail-recommend__empty-icon"
                  src={noDataIcon}
                  alt=""
                />
                <p className="news-detail-recommend__empty-text">暂无数据</p>
              </div>
            ) : null}
          </section>
        </aside>
      </div>

      {showPager ? (
        <section className="news-detail-pager" aria-label="上一篇 / 下一篇">
          <span className="news-detail-pager__line" aria-hidden="true" />

          <div className="news-detail-pager__row">
            <NewsPagerItem
              article={hasPrev ? prev : null}
              direction="prev"
              disabled={!hasPrev}
              placeholder="已是最早的文章"
              onOpenDetail={onOpenDetail}
            />

            <span className="news-detail-pager__divider" aria-hidden="true" />

            <NewsPagerItem
              article={hasNext ? next : null}
              direction="next"
              disabled={!hasNext}
              placeholder="已是最新文章"
              onOpenDetail={onOpenDetail}
            />
          </div>

          <span className="news-detail-pager__line" aria-hidden="true" />
        </section>
      ) : null}

      {visibleLatest.length > 0 ? (
        <section className="news-detail-latest" aria-label="最新内容推荐">
          <RecommendHead title="最新内容推荐" />

          <div className="news-detail-latest__grid">
            {visibleLatest.map((item) => (
              <NewsCard
                key={item.id}
                listMode={false}
                brandName="BUJUE"
                typeName={item.categoryLabel || ""}
                title={item.title || ""}
                coverUrl={item.cover || ""}
                pinned={Boolean(item.pinned)}
                category={item.categoryLabel || ""}
                summary={item.summary || ""}
                teamName={item.author || ""}
                publishDate={formatNewsPublishDate(item.tmPublished)}
                onClick={() => onOpenDetail?.(item.id, item.title)}
              />
            ))}
          </div>
        </section>
      ) : null}

      <section className="news-detail-support" aria-label="获取服务支持">
        <div className="news-detail-support__content">
          <span className="news-detail-support__eyebrow">
            BUJUE · CONTENT
          </span>
          <p className="news-detail-support__title">
            让好内容，持续创造价值
          </p>
          <p className="news-detail-support__desc">
            发现更多关于品牌、产品与创造力的思考。
          </p>
        </div>

        <a
          href="https://v.wjx.cn/vm/PD6aaEY.aspx#"
          className="news-detail-support__cta"
          target="_blank"
          rel="noopener noreferrer"
          onClick={handleSupportOpen}
        >
          获取服务支持
        </a>
      </section>

      {/* 回到顶部：Figma 3149:4432（48 圆 + 白 80% 底 + 背景模糊 + 25px 投影）。
          外层 0 高锚点用 sticky 贴容器底部：容器底部还在视口下方时，按钮贴在屏幕底部 30；
          容器底部滚进视口后，按钮停在距容器底部 30 并随容器一起往上滚。
          锚点 0 高，不给页面额外增加高度。 */}
      <div className="news-detail-backtop">
        <ScrollTopBtn
          container={document}
          x={32}
          y={30}
          position="absolute"
        />
      </div>
    </div>
  );
}
